// FULL FILE — frontend/src/features/shopfloor/pages/DowntimeBoardPage.tsx
// AM-032: additive changes only, marked with "// AM-032" comments.
// Everything else is byte-for-byte what was read from the real file.

import { useState, useEffect } from "react";
import { useV1ProductionWorkCentersList } from "@/api/generated/v1/v1";
import {
  useDowntimeReasonCodesList,
  useDowntimeRecordsList,
  useEndDowntime,
  useStartDowntime,
} from "../api";
import { useShopfloorStore } from "../store/shopfloorStore";
import { PageBanner } from "@/shared/components/PageBanner";
// AM-032: reuse the same embeddable Custom Fields panel already wired into
// ProductionOrderCreatePage.tsx / MaterialCreatePage.tsx (ADR-095, AM-009).
import { CustomFieldsPanel } from "@/features/customFields/CustomFieldsPanel";
import { setCustomFieldValues } from "@/features/customFields/api";

// AM-032: DowntimeRecord lives in the SEPARATE "apps.production.shopfloor"
// Django app (AppConfig.label = "production_shopfloor"), NOT "production" —
// see AM-032 doc §2.1. This constant is the single source of truth for that
// string in this file so it can't drift from CustomFieldsManagerPage.tsx.
const DOWNTIME_RECORD_TARGET_TYPE = "production_shopfloor.downtimerecord";

export function DowntimeBoardPage() {
  const selectedWorkCenterId = useShopfloorStore((s) => s.selectedWorkCenterId);
  const { data: reasons = [] } = useDowntimeReasonCodesList();
  const { data: records = [], isLoading } = useDowntimeRecordsList(selectedWorkCenterId ?? undefined);
  const startDowntime = useStartDowntime();
  const endDowntime = useEndDowntime();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [modalWorkCenterId, setModalWorkCenterId] = useState(selectedWorkCenterId || "");
  const [reasonCodeId, setReasonCodeId] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  // AM-032: custom-field draft values collected while the "start downtime"
  // modal is open, saved only after the DowntimeRecord itself is created
  // (same deferred-save pattern as ProductionOrderCreatePage.tsx).
  const [customFieldValues, setCustomFieldValuesState] = useState<Record<string, any>>({});
  const [customFieldsValid, setCustomFieldsValid] = useState(true);

  const { data: workCentersData, isLoading: wcLoading } = useV1ProductionWorkCentersList();
  const workCenters = workCentersData?.results || [];

  // Update modal work center if global selection changes
  useEffect(() => {
    if (selectedWorkCenterId) {
      setModalWorkCenterId(selectedWorkCenterId);
    }
  }, [selectedWorkCenterId]);

  // Filters state
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all, active, completed

  const openRecords = records.filter((r) => !r.ended_at);

  const handleStart = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!modalWorkCenterId) {
      setError("ابتدا ایستگاه کاری را از انتخاب کنید.");
      return;
    }
    if (!reasonCodeId) {
      setError("علت توقف باید انتخاب شود.");
      return;
    }
    if (!customFieldsValid) {
      // AM-032: mirrors the same guard used on ProductionOrderCreatePage.tsx.
      setError("لطفاً فیلدهای اجباری فیلدهای سفارشی را پر کنید.");
      return;
    }
    try {
      // AM-032: useStartDowntime is a hand-written (non-orval) hook whose
      // mutationFn returns the raw axios response, NOT unwrapped data — see
      // features/shopfloor/api.ts. The record id is therefore `res.data.id`,
      // NOT `res.id` (unlike the orval-generated create hooks used on
      // ProductionOrderCreatePage.tsx). Getting this wrong makes the
      // second call below silently save against `undefined`.
      const res = await startDowntime.mutateAsync({
        work_center: modalWorkCenterId,
        reason_code: reasonCodeId,
        notes,
      });
      const newRecordId = (res as any)?.data?.id;

      // AM-032: known, documented risk (AM-032 doc §2.3) — this is a second,
      // separate API call, not part of a DB transaction with the create
      // above. If it fails, the DowntimeRecord already exists without its
      // custom field values; the user sees the error and can re-enter them
      // via the custom fields panel on the record later.
      if (newRecordId && Object.keys(customFieldValues).length > 0) {
        try {
          await setCustomFieldValues(DOWNTIME_RECORD_TARGET_TYPE, newRecordId, customFieldValues);
        } catch (cfErr) {
          setError(
            "توقف ثبت شد، اما ذخیرهٔ فیلدهای سفارشی با خطا مواجه شد: " +
              (cfErr instanceof Error ? cfErr.message : String(cfErr)),
          );
        }
      }

      setNotes("");
      setReasonCodeId("");
      setCustomFieldValuesState({});
      setCustomFieldsValid(true);
      setIsCreateModalOpen(false);
    } catch {
      setError("یک رکورد توقف باز برای این ایستگاه کاری از قبل وجود دارد.");
    }
  };

  const handleEnd = async (recordId: string) => {
    await endDowntime.mutateAsync(recordId);
  };

  const filteredRecords = records.filter(r => {
    if (statusFilter === "active" && r.ended_at) return false;
    if (statusFilter === "completed" && !r.ended_at) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      if (!r.reason_code.toLowerCase().includes(search) && !r.notes?.toLowerCase().includes(search)) {
        return false;
      }
    }
    return true;
  });

  return (
    <div
      dir="rtl"
      style={{
        padding: "0.5rem 0",
        maxWidth: "100%",
        margin: "0",
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
        fontFamily: "var(--font-family-fa, sans-serif)",
      }}
    >
      <PageBanner
        title="پیگیری توقفات تولید (Downtime)"
        subtitle="ثبت و مدیریت علل توقف ایستگاه‌های کاری و ماشین‌آلات خط تولید"
        icon="⏸️"
        helpLink="/help/shopfloor/downtimes"
      >
        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "center" }}>
          <button
            type="button"
            onClick={() => setIsCreateModalOpen(true)}
            style={{
              padding: "0.6rem 1.2rem",
              borderRadius: "12px",
              background: "#ffffff",
              color: "#007270",
              border: "1px solid rgba(255, 255, 255, 0.8)",
              fontWeight: 700,
              fontSize: "0.9rem",
              cursor: "pointer",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              transition: "all 0.2s ease",
            }}
          >
            <span>⛔</span>
            <span>ثبت توقف جدید</span>
          </button>
        </div>
      </PageBanner>

      {error && (
        <div style={{ padding: "0.85rem 1.1rem", borderRadius: "10px", background: "rgba(239, 68, 68, 0.12)", border: "1px solid #EF4444", color: "#B91C1C", fontSize: "0.85rem", fontWeight: 600 }}>
          🚨 {error}
        </div>
      )}

      {/* KPI Cards / Current status summary */}
      {openRecords.map((openRecord) => (
        <div key={openRecord.id} style={{ padding: "1.5rem", background: "rgba(239, 68, 68, 0.05)", border: "1px solid #EF4444", borderRadius: "14px", display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: "0 10px 30px rgba(239, 68, 68, 0.1)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <span style={{ fontSize: "2rem", background: "#EF4444", color: "#FFF", width: "50px", height: "50px", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center" }}>
              ⚠️
            </span>
            <div>
              <p style={{ fontWeight: 800, color: "#B91C1C", fontSize: "1.1rem", margin: "0 0 0.25rem 0" }}>
                {openRecord.machine_display?.name || openRecord.work_center_display?.name || "ماشین/ایستگاه"} در وضعیت توقف قرار دارد!
              </p>
              <p style={{ color: "#7F1D1D", margin: 0, fontSize: "0.9rem" }}>از تاریخ: <span style={{ direction: "ltr", display: "inline-block", fontWeight: "bold" }}>{new Date(openRecord.started_at).toLocaleString('fa-IR')}</span> | علت: {openRecord.reason_code_display?.description || reasons.find(r => r.id === openRecord.reason_code)?.description || openRecord.reason_code}</p>
            </div>
          </div>
          <button
            onClick={() => handleEnd(openRecord.id)}
            disabled={endDowntime.isPending}
            style={{
              padding: "0.8rem 2rem",
              borderRadius: "10px",
              background: "#EF4444",
              color: "#ffffff",
              border: "none",
              fontWeight: 700,
              fontSize: "1rem",
              cursor: endDowntime.isPending ? "not-allowed" : "pointer",
              boxShadow: "0 4px 10px rgba(239, 68, 68, 0.3)",
              transition: "transform 0.2s"
            }}
          >
            {endDowntime.isPending ? "درحال ثبت..." : "پایان توقف"}
          </button>
        </div>
      ))}

      {/* CONTROL BAR */}
      <div className="brand-card-extand" style={{ padding: "1rem 1.25rem", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "1rem" }}>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button style={{ padding: "6px 12px", borderRadius: "8px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", fontSize: "0.8rem", fontWeight: 700, color: "var(--color-text-main, #2B2B2B)", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px" }}>
            <span>🖨️</span> چاپ
          </button>
          <button style={{ padding: "6px 12px", borderRadius: "8px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", fontSize: "0.8rem", fontWeight: 700, color: "var(--color-text-main, #2B2B2B)", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px" }}>
            <span>📄</span> خروجی PDF
          </button>
          <button style={{ padding: "6px 12px", borderRadius: "8px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", fontSize: "0.8rem", fontWeight: 700, color: "var(--color-text-main, #2B2B2B)", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px" }}>
            <span>📊</span> خروجی Excel
          </button>
        </div>
        
        <div style={{ display: "flex", gap: "0.75rem", flex: 1, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <select 
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ padding: "6px 12px", borderRadius: "8px", border: "1px solid var(--color-border, #E4DCC8)", fontSize: "0.85rem", outline: "none", minWidth: "150px" }}
          >
            <option value="all">وضعیت (همه)</option>
            <option value="active">توقف‌های در جریان</option>
            <option value="completed">توقف‌های پایان‌یافته</option>
          </select>
          <input 
            type="text" 
            placeholder="جستجو..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ padding: "6px 12px", borderRadius: "8px", border: "1px solid var(--color-border, #E4DCC8)", fontSize: "0.85rem", outline: "none", minWidth: "200px" }}
          />
        </div>
      </div>

      <div className="brand-card-extand" style={{ padding: "0", overflow: "hidden" }}>
        {isLoading ? (
          <div style={{ padding: "3rem", textAlign: "center", color: "var(--color-text-muted)", fontWeight: 600 }}>در حال بارگذاری...</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
              <thead>
                <tr style={{ background: "rgba(0,0,0,0.02)", borderBottom: "1px solid var(--color-border, #E4DCC8)", color: "var(--color-text-main, #2B2B2B)" }}>
                  <th style={{ padding: "1rem", textAlign: "right", fontWeight: 800 }}>شمارهٔ رکورد</th>
                  <th style={{ padding: "1rem", textAlign: "right", fontWeight: 800 }}>ایستگاه / ماشین</th>
                  <th style={{ padding: "1rem", textAlign: "right", fontWeight: 800 }}>علت توقف</th>
                  <th style={{ padding: "1rem", textAlign: "right", fontWeight: 800 }}>وضعیت</th>
                  <th style={{ padding: "1rem", textAlign: "right", fontWeight: 800 }}>زمان شروع</th>
                  <th style={{ padding: "1rem", textAlign: "center", fontWeight: 800 }}>زمان پایان</th>
                  <th style={{ padding: "1rem", textAlign: "center", fontWeight: 800 }}>مدت (دقیقه)</th>
                  <th style={{ padding: "1rem", textAlign: "right", fontWeight: 800 }}>توضیحات</th>
                  <th style={{ padding: "1rem", textAlign: "center", fontWeight: 800 }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.map((r, index) => (
                  <tr key={r.id} style={{ borderBottom: "1px solid rgba(0,0,0,0.05)", transition: "background 0.2s" }} className="hover:bg-gray-50/50">
                    <td style={{ padding: "1rem", fontWeight: 700, color: "#555" }}>#{records.length - index}</td>
                    <td style={{ padding: "1rem", fontWeight: 700, color: "#2B2B2B" }}>{r.machine_display?.name || r.work_center_display?.name || "-"}</td>
                    <td style={{ padding: "1rem", fontWeight: 700, color: "#007270" }}>{r.reason_code_display?.description || reasons.find(reason => reason.id === r.reason_code)?.description || r.reason_code}</td>
                    <td style={{ padding: "1rem" }}>
                      {!r.ended_at ? (
                        <span style={{ background: "rgba(239, 68, 68, 0.15)", color: "#B91C1C", padding: "4px 8px", borderRadius: "6px", fontSize: "0.75rem", fontWeight: 700 }}>در جریان</span>
                      ) : (
                        <span style={{ background: "rgba(16, 185, 129, 0.15)", color: "#10B981", padding: "4px 8px", borderRadius: "6px", fontSize: "0.75rem", fontWeight: 700 }}>پایان یافته</span>
                      )}
                    </td>
                    <td style={{ padding: "1rem", direction: "ltr", textAlign: "right", fontSize: "0.85rem", color: "#666" }}>{new Date(r.started_at).toLocaleString('fa-IR')}</td>
                    <td style={{ padding: "1rem", direction: "ltr", textAlign: "center", fontSize: "0.85rem", color: "#666" }}>{r.ended_at ? new Date(r.ended_at).toLocaleString('fa-IR') : "---"}</td>
                    <td style={{ padding: "1rem", textAlign: "center", fontWeight: 700, color: "#444" }}>{r.duration_minutes != null ? r.duration_minutes : "---"}</td>
                    <td style={{ padding: "1rem", color: "#555", fontSize: "0.85rem" }}>{r.notes || "-"}</td>
                    <td style={{ padding: "1rem", textAlign: "center" }}>
                      {!r.ended_at ? (
                        <button
                          onClick={() => handleEnd(r.id)}
                          disabled={endDowntime.isPending}
                          style={{
                            padding: "6px 12px",
                            borderRadius: "8px",
                            background: "#EF4444",
                            color: "#ffffff",
                            border: "none",
                            fontWeight: 700,
                            fontSize: "0.75rem",
                            cursor: endDowntime.isPending ? "not-allowed" : "pointer",
                            transition: "background 0.2s"
                          }}
                        >
                          پایان توقف
                        </button>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                ))}
                {filteredRecords.length === 0 && (
                  <tr>
                    <td colSpan={9} style={{ padding: "3rem", textAlign: "center", color: "var(--color-text-muted)", fontWeight: 600 }}>هیچ توقفی با این مشخصات یافت نشد.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* CREATE DOWNTIME MODAL */}
      {isCreateModalOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.5)",
            backdropFilter: "blur(4px)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
          }}
        >
          <div
            className="brand-card-extand"
            style={{
              padding: "1.5rem",
              width: "100%",
              maxWidth: "500px",
              boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "0.75rem", marginBottom: "1.5rem", borderBottom: "1px solid var(--color-border, #E4DCC8)" }}>
              <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, color: "#007270" }}>⛔ ثبت توقف تولید جدید</h3>
              <button onClick={() => setIsCreateModalOpen(false)} style={{ background: "none", border: "none", color: "#888", fontSize: "1.2rem", cursor: "pointer" }}>
                ✕
              </button>
            </div>

            <form onSubmit={handleStart} style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
              <div>
                <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "6px" }}>ایستگاه کاری متوقف شده</label>
                <select 
                  value={modalWorkCenterId} 
                  onChange={(e) => setModalWorkCenterId(e.target.value)} 
                  required
                  style={{ width: "100%", padding: "10px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.9rem", outline: "none" }}
                >
                  <option value="">-- انتخاب ایستگاه کاری --</option>
                  {wcLoading ? (
                    <option disabled>در حال بارگذاری...</option>
                  ) : (
                    workCenters.map((wc: any) => (
                      <option key={wc.id} value={wc.id}>{wc.name} ({wc.code})</option>
                    ))
                  )}
                </select>
              </div>

              <div>
                <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "6px" }}>علت توقف</label>
                <select 
                  value={reasonCodeId} 
                  onChange={(e) => setReasonCodeId(e.target.value)} 
                  required
                  style={{ width: "100%", padding: "10px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.9rem", outline: "none" }}
                >
                  <option value="">-- انتخاب علت --</option>
                  {reasons.length === 0 ? (
                    <option disabled>⚠️ هیچ علت توقفی سیستمی تعریف نشده است</option>
                  ) : (
                    reasons.map((r) => (
                      <option key={r.id} value={r.id}>{r.code} - {r.description}</option>
                    ))
                  )}
                </select>
              </div>

              <div>
                <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "6px" }}>توضیحات تکمیلی (اختیاری)</label>
                <textarea 
                  placeholder="شرح جزئیات خرابی یا توقف..." 
                  value={notes} 
                  onChange={(e) => setNotes(e.target.value)}
                  style={{ width: "100%", minHeight: "100px", padding: "10px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.9rem", outline: "none", resize: "vertical" }}
                />
              </div>

              {/* AM-032: custom fields collected here, saved after the record
                  is created in handleStart above (see AM-032 doc §2.3 for the
                  known non-atomic risk this inherits from AM-009). */}
              <CustomFieldsPanel
                targetContentType={DOWNTIME_RECORD_TARGET_TYPE}
                hideSaveButton
                onChange={(values, isValid) => {
                  setCustomFieldValuesState(values);
                  setCustomFieldsValid(isValid);
                }}
              />

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "0.5rem", paddingTop: "1rem", borderTop: "1px solid var(--color-border, #E4DCC8)" }}>
                <button 
                  type="button" 
                  onClick={() => setIsCreateModalOpen(false)} 
                  style={{ padding: "8px 16px", borderRadius: "10px", background: "none", border: "1px solid var(--color-border, #E4DCC8)", color: "#555", fontSize: "0.85rem", cursor: "pointer", fontWeight: 700 }}
                >
                  انصراف
                </button>
                <button 
                  type="submit" 
                  disabled={startDowntime.isPending} 
                  style={{ padding: "8px 24px", borderRadius: "10px", background: "#F59E0B", color: "#FFF", border: "none", fontSize: "0.9rem", fontWeight: 800, cursor: startDowntime.isPending ? "not-allowed" : "pointer" }}
                >
                  {startDowntime.isPending ? "در حال ثبت..." : "ثبت شروع توقف"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
